import {merge} from '#core/utils/config-utils.js';
import {icon} from '#core/utils/style-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';

import {generateAvantgardeOddsButton} from '#core/application/modules/cards-and-promotions/avantgarde-cards/avantgarde-event-card-common.js';
import {generateBetBuilderSelectionItem} from '#core/application/modules/tiles/bet-builder-selection-item/index.js';
import {generateBorder} from '#core/application/modules/tiles/border/index.js';
import {generateSecondaryLink} from '#core/application/modules/tiles/link/index.js';
import {generateNeutralVenueIndicator} from '#core/application/modules/tiles/neutral-venue-indicator/index.js';

const SECTION_HEADER_BORDER = {
    borderStyle: 'solid',
    borderWidth: '0 0 1px 0',
};

const SECTION_FOOTER_BORDER = {
    borderStyle: 'solid',
    borderWidth: '1px 0 0 0',
};

const ROW_DIVIDER_BORDER = {
    borderStyle: 'solid',
    borderWidth: '0 0 1px 0',
};

export function avantgardePreBuiltCardsFactory(PALETTE) {
    const SHOW_ALL_SELECTIONS_BUTTON = merge(generateSecondaryLink(PALETTE.PREBUILT_CARD_DETAILS.SHOW_MORE_BUTTON), {
        button: {
            style: {
                base: {
                    alignItems: 'center',
                },
            },
        },
    });

    const SECTION_BAND_STYLE = {
        ...SECTION_HEADER_BORDER,
        borderColor: PALETTE.PREBUILT_SECTION_DIVIDER_COLOR,
        ...resolveBackground(PALETTE.PREBUILT_SECTION_HEADER_BG),
    };

    const HEADER_TEXT_CONTAINER_STYLE = {
        ...SECTION_HEADER_BORDER,
        borderColor: PALETTE.PREBUILT_SECTION_DIVIDER_COLOR,
    };

    return {
        PrebuiltBetController: {
            showTeamKits: false,
        },
        PrebuiltBetBuilderAvantgarde: {
            headerContainer: {
                style: {
                    flexGrow: 0,
                },
            },
            sportIcon: {
                icon: {
                    style: {
                        fill: PALETTE.PREBUILT_CARD_DETAILS.SPORT_ICON_COLOR,
                    },
                },
            },
            typeAndTimeHeader: {
                style: SECTION_BAND_STYLE,
            },
            headerTextContainer: {
                style: HEADER_TEXT_CONTAINER_STYLE,
            },
            typeName: {
                style: PALETTE.PREBUILT_CARD_DETAILS.EVENT_INFO_TEXT,
            },
            startTime: {
                style: PALETTE.PREBUILT_CARD_DETAILS.EVENT_INFO_TEXT,
            },
            promotionText: {
                style: PALETTE.PREBUILT_CARD_DETAILS.EVENT_NAME_TEXT,
            },
            participantsIconInline: {
                participantName: {
                    style: PALETTE.PREBUILT_CARD_DETAILS.EVENT_NAME_TEXT,
                },
                separator: {
                    style: PALETTE.PREBUILT_CARD_DETAILS.EVENT_NAME_TEXT,
                },
            },
            signpostingIcons: {
                earlyPayoutIndicator: {
                    labeledTooltip: {
                        icon: icon(PALETTE.PREBUILT_CARD_DETAILS.BB_NEUTRAL_VENUE_INDICATOR.NVI_ICON_COLOR),
                    },
                },
            },
            neutralVenueIndicator: generateNeutralVenueIndicator(
                PALETTE.PREBUILT_CARD_DETAILS.BB_NEUTRAL_VENUE_INDICATOR
            ),
        },
        PrebuiltAccaAvantgarde: {
            view: {
                footerContainer: {
                    style: {
                        ...SECTION_FOOTER_BORDER,
                        borderColor: PALETTE.PREBUILT_SECTION_DIVIDER_COLOR,
                        ...resolveBackground(PALETTE.PREBUILT_SECTION_HEADER_BG),
                    },
                },
                matchAccaSelectionItem: {
                    rowDivider: {
                        style: {
                            ...ROW_DIVIDER_BORDER,
                            borderColor: PALETTE.PREBUILT_SECTION_DIVIDER_COLOR,
                        },
                    },
                    eventName: {
                        style: PALETTE.PREBUILT_ACCA_ROW_EVENT_TEXT,
                    },
                    selectionLabel: {
                        style: PALETTE.PREBUILT_ACCA_ROW_SELECTION_TEXT,
                    },
                    eventStartTime: {
                        style: PALETTE.PREBUILT_ACCA_ROW_EVENT_TEXT,
                    },
                    price: {
                        style: PALETTE.PREBUILT_ACCA_ROW_PRICE_TEXT,
                    },
                    basePrice: {
                        style: PALETTE.PREBUILT_ACCA_ROW_BASE_PRICE_TEXT,
                    },
                },
            },
            headerSection: {
                style: SECTION_BAND_STYLE,
            },
            titleContainer: {
                style: PALETTE.PREBUILT_ACCA_TITLE_TEXT,
            },
            titleIcon: {
                style: {
                    fill: 'none',
                },
            },
            badgeContainer: {
                style: {
                    borderRadius: '4px',
                    ...resolveBackground(PALETTE.PREBUILT_ACCA_BADGE_BG),
                },
            },
            badge: {
                style: PALETTE.PREBUILT_ACCA_BADGE_TEXT,
            },
            promotionText: {
                style: PALETTE.PREBUILT_ACCA_PROMOTION_TEXT,
            },
        },
        PrebuiltBetAvantgarde: {
            matchAccaSelectionsContainer: {
                style: {
                    flexGrow: 0,
                },
            },
            matchAccaSelectionItem: generateBetBuilderSelectionItem(PALETTE.PREBUILT_CARD_DETAILS.SELECTION_ITEM),
            betCountValueContainer: {
                style: {
                    borderRadius: '9999px',
                    ...resolveBackground(PALETTE.PREBUILT_BET_COUNT_PILL_BG),
                },
            },
            betCountValue: {
                style: PALETTE.PREBUILT_BET_COUNT_VALUE_TEXT,
            },
            betCountLabel: {
                style: PALETTE.PREBUILT_BET_COUNT_LABEL_TEXT,
            },
            selectionButton: generateAvantgardeOddsButton(PALETTE.PREBUILT_CARD_DETAILS.ODDS_BUTTON),
            boostedSelectionButton: merge(generateAvantgardeOddsButton(PALETTE.PREBUILT_CARD_DETAILS.ODDS_BUTTON), {
                view: {
                    borderStyle: {
                        borderRadius: PALETTE.PREBUILT_BOOST_ODDS_BORDER_RADIUS,
                    },
                    containerInactive: {
                        ...resolveBackground(PALETTE.PREBUILT_BOOST_ODDS_BG),
                    },
                    containerInactivehover: {
                        ...resolveBackground(PALETTE.PREBUILT_BOOST_ODDS_BG),
                    },
                    containerActive: {
                        ...resolveBackground(PALETTE.PREBUILT_BOOST_ODDS_BG),
                    },
                    containerActivehover: {
                        ...resolveBackground(PALETTE.PREBUILT_BOOST_ODDS_BG),
                    },
                    priceBoostIndicator: {
                        indicationIcon: icon(PALETTE.PREBUILT_BOOST_INDICATOR_ICON_COLOR),
                        previousValue: {
                            style: PALETTE.PREBUILT_BOOST_PREVIOUS_PRICE_TEXT,
                        },
                    },
                    priceText: {
                        base: PALETTE.PREBUILT_BOOST_PRICE_TEXT,
                        inactive: PALETTE.PREBUILT_BOOST_PRICE_TEXT,
                    },
                },
            }),
            showAllSelectionsButton: {
                showAllButton: SHOW_ALL_SELECTIONS_BUTTON,
                hideAllButton: SHOW_ALL_SELECTIONS_BUTTON,
            },
        },
        PrebuiltBetsSkeleton: {
            container: {
                style: {
                    padding: '0 0 2px 0',
                    _mobile_padding: '0 8px 2px 0',
                },
            },
            cardContainer: {
                style: {
                    ...resolveBackground(PALETTE.PREBUILT_CARD_CONTAINER_BG),
                    ...generateBorder(PALETTE.PREBUILT_CARD_BORDER),
                },
            },
        },
    };
}
